/**
 * src/components/auth/CreateSpaceModal.tsx
 * =============================================================================
 * Modal para crear un espacio nuevo. Pro-only — si el user es free, el
 * backend devuelve 402 y mostramos CTA a /precios sin permitir submit.
 *
 * Tras crear el espacio:
 *  - Refrescamos la lista del context (refresh()).
 *  - Marcamos el espacio recién creado como activo automáticamente —
 *    el user va a querer trabajar ahí enseguida.
 * =============================================================================
 */

import { useRef, useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { Trans, useTranslation } from 'react-i18next'
import { Loader2, X } from 'lucide-react'
import { useFocusTrap } from '@/hooks/useFocusTrap'
import { createSpace, SpacesError } from '@/services/spacesService'
import { getCheckout } from '@/services/billingService'
import { useActiveSpace } from '@/components/providers/ActiveSpaceProvider'

interface Props {
  onClose: () => void
}

export function CreateSpaceModal({ onClose }: Props) {
  const { t } = useTranslation()
  const { refresh, setActiveSpaceId } = useActiveSpace()
  const [name, setName] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [errorCode, setErrorCode] = useState<string | null>(null)
  /** Si Polar falla después de crear la fila, el reintento reutiliza este id.
   *  Sin él, cada click posterior crearía otro espacio huérfano. */
  const [createdSpaceId, setCreatedSpaceId] = useState<string | null>(null)
  /** Trampa de foco: Tab cicla dentro y al cerrar vuelve al disparador. */
  const dialogRef = useRef<HTMLDivElement>(null)
  useFocusTrap(dialogRef)

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    if (submitting) return
    const trimmed = name.trim()
    if (trimmed.length < 2) {
      setErrorCode('invalid_name')
      return
    }
    setSubmitting(true)
    setErrorCode(null)
    let spaceId = createdSpaceId
    try {
      if (!spaceId) {
        spaceId = (await createSpace(trimmed)).id
        setCreatedSpaceId(spaceId)
        await refresh()
        setActiveSpaceId(spaceId)
      }
    } catch (err) {
      const code = err instanceof SpacesError ? err.code : 'space_create_failed'
      setErrorCode(code)
      setSubmitting(false)
      return
    }

    try {
      // Crear y activar es una sola promesa para la persona. La fila se crea
      // antes porque Polar necesita su id en metadata; si cancela el pago, el
      // espacio queda visible como "Sin activar" y puede retomarlo después.
      const checkout = await getCheckout('space_base', { spaceId })
      if (checkout.action !== 'checkout') {
        window.location.assign('/?spaceActivated=1')
        return
      }
      if (!checkout.url) throw new Error('checkout_without_url')
      window.location.assign(checkout.url)
    } catch {
      setErrorCode('checkout_failed')
      setSubmitting(false)
    }
  }

  const isProRequired = errorCode === 'pro_required'

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-label={t('panel.crearEspacio')}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
      className="fixed inset-0 z-50 bg-black/20 flex items-center justify-center p-4"
    >
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl border border-zinc-200 flex flex-col overflow-hidden">
        <header className="flex items-center justify-between px-5 h-14 border-b border-zinc-100 shrink-0">
          <h2 className="text-base font-semibold text-zinc-900">{t('crearEspacio.titulo')}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('actions.close')}
            className="h-8 w-8 flex items-center justify-center rounded-md text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900"
          >
            <X className="h-5 w-5" strokeWidth={1.8} />
          </button>
        </header>

        <div className="px-5 py-4">
          {isProRequired ? (
            <div className="text-center space-y-3 py-3">
              <p className="text-sm text-zinc-900">
                {t('crearEspacio.proRequerido.titulo')}
              </p>
              <p className="text-xs text-zinc-500 leading-relaxed">
                {t('crearEspacio.proRequerido.texto')}
              </p>
              <Link
                to="/precios"
                onClick={onClose}
                className="inline-block text-sm font-medium text-zinc-900 underline underline-offset-4"
              >
                {t('actions.verPlanesFlecha')}
              </Link>
            </div>
          ) : (
            <form onSubmit={(e) => void onSubmit(e)} className="space-y-3">
              <p className="text-xs text-zinc-500 leading-relaxed">
                {t('crearEspacio.intro')}
              </p>
              {/* El precio se enseña antes del botón que abre el checkout: no
                  puede parecer que el aviso informativo ya ha cobrado nada. */}
              <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-3 text-xs text-zinc-700 leading-relaxed">
                <p className="font-medium text-zinc-900 mb-1">
                  {t('crearEspacio.precioTitulo')}
                </p>
                <p>
                  {/* Trans conserva las negritas del precio y de "miembros
                      ilimitados" respetando el orden de cada idioma. */}
                  <Trans i18nKey="crearEspacio.precioTexto" components={{ b: <strong /> }} />
                </p>
                {/* La ampliación existe, pero no se mezcla con la decisión de
                    crear el espacio: se ofrece después desde su barra de cuota. */}
                <p className="mt-1 text-zinc-500">
                  {t('crearEspacio.precioNota')}
                </p>
              </div>
              <div>
                <label
                  htmlFor="space-name"
                  className="block text-xs font-medium text-zinc-700 mb-1"
                >
                  {t('crearEspacio.nombreLabel')}
                </label>
                <input
                  id="space-name"
                  type="text"
                  required
                  // La trampa de foco enfoca esto al abrir (en vez de la X);
                  // sustituye al antiguo autoFocus, que no devolvía el foco.
                  data-autofocus
                  maxLength={80}
                  value={name}
                  disabled={createdSpaceId !== null}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t('crearEspacio.nombrePlaceholder')}
                  className="w-full h-10 px-3 rounded-md border border-zinc-200 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-300"
                />
              </div>

              {errorCode && !isProRequired && (
                <p className="text-xs text-red-600" role="alert">
                  {errorCode === 'invalid_name'
                    ? t('crearEspacio.errores.invalid_name')
                    : errorCode === 'checkout_failed'
                      ? t('crearEspacio.errores.checkout_failed')
                      : t('crearEspacio.errores.generic')}
                </p>
              )}

              <div className="flex justify-end gap-2 pt-1">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={submitting}
                  className="text-sm px-3 h-9 rounded-md text-zinc-700 hover:bg-zinc-100"
                >
                  {t('actions.cancelar')}
                </button>
                <button
                  type="submit"
                  disabled={submitting || name.trim().length < 2}
                  className="inline-flex items-center gap-1.5 text-sm px-3 h-9 rounded-md bg-zinc-900 text-white hover:bg-zinc-800 disabled:opacity-50"
                >
                  {submitting && (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={2} />
                  )}
                  {createdSpaceId ? t('crearEspacio.reintentarPago') : t('crearEspacio.continuarPago')}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  )
}
